/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { Check, CircleStop, FlipHorizontal2, FolderOpen, ImageOff, Images, ListX, Loader2, RotateCcw, Upload, X } from 'lucide-react';
import { api } from '../lib/api';
import {
  BULK_MAX_FILES,
  LOST_ANSWER_TEXT,
  MIRROR_WARNING,
  NO_ANSWER_TEXT,
  addedSince,
  bulkStatuses,
  countLine,
  filesFromEntries,
  intake,
  isUploadable,
  leftOutText,
  nameFromFileName,
  newCategory,
  roomOf,
  roomText,
  statusLabel,
  unreadableAnalysis,
  uploadErrorAction,
  uploadSummaryText,
  type BulkFileAnalysis,
  type BulkResult,
  type BulkRowState,
  type BulkStatus,
  type LeftOut,
} from '../lib/bulkOutfits';
import {
  MASCOT_DRAW_HINT,
  MASCOT_LIMITS,
  bytesToBase64,
  defaultWindowInputs,
  formatBytes,
  fullText,
  mascotAuthText,
  mascotErrorCode,
  mascotErrorText,
  outfitAdd,
  windowFromInputs,
  windowRefusal,
  type DesktopMascotCatalog,
  type WindowInputs,
} from '../lib/desktopMascot';
import { errorText } from '../lib/errors';
import { CATEGORY_NAME_MAX, GENERAL_CATEGORY_ID, GENERAL_CATEGORY_NAME, categoryNameOf, type CategoriesView } from '../lib/outfitCategories';
import { Button, Callout, Checkbox, Field, Icon, Input, Meter, Panel, Select, StatusChip, type Tone } from '../ui';
import { analyzeOutfitFile } from './mascotCanvas';
import { clockNow, wallpaperClass, type Wallpaper } from './mascotUi';
import { SaveNote } from './SaveNote';
import { WindowFields } from './WindowFields';

export interface BulkOutfitsSectionProps {
  /** The catalog the card shows: its revision, its outfits (names and images already there) and its limits. */
  catalog: DesktopMascotCatalog;
  now: number;
  timeZone: string;
  /** The card's preview wallpaper, behind each picture in the list. */
  wallpaper: Wallpaper;
  /** Nothing can be added here: the admin is an auditor, the gallery is full, or the service cannot tell day outfits apart. */
  disabled: boolean;
  /** The card is saving another change: Add waits for it. */
  waiting: boolean;
  /** Each answer's catalog, so the gallery above fills in while the upload runs. */
  onCatalog: (catalog: DesktopMascotCatalog) => void;
  /** True while the upload runs: the card keeps its other changes off meanwhile. */
  onBusy: (busy: boolean) => void;
  /** Read the catalog again: after the upload, and after a new category is saved. */
  reload: () => Promise<unknown>;
  /** How each file is checked and prepared (the canvas work, analyzeOutfitFile); the render tests pass their own. */
  analyze?: (file: File, mirror: boolean) => Promise<BulkFileAnalysis>;
}

/** One file in the list. */
interface Row {
  key: string;
  file: File;
  /** The outfit's name, from the file name until the admin changes it. */
  name: string;
  /** Mirror (face left): prepared mirrored. */
  mirror: boolean;
  /** Raised whenever the file must be checked again (Mirror changed). */
  token: number;
  /** The latest check and the token it was made for; while a newer check runs, its picture stays, dimmed. */
  checked: { token: number; analysis: BulkFileAnalysis } | null;
  /** Add it although an outfit with its name is in the gallery or higher in the list. */
  addAnyway: boolean;
  result: BulkResult | null;
}

type Note = { ok: boolean; text: string };

/** The Category select's last option: opens the field for a new category. */
const NEW_CATEGORY = '__new__';
/** Where the focus goes after the last row is removed. */
const CHOOSE_FILES = '__choose__';
const STATUS_TONE: Record<BulkStatus['kind'], Tone> = {
  checking: 'neutral',
  ready: 'ok',
  prepared: 'info',
  'same-name': 'warn',
  name: 'warn',
  refused: 'bad',
  'no-room': 'warn',
  uploading: 'info',
  added: 'ok',
  failed: 'bad',
};

/** The check that is current for a row: null while it is (again) being checked. */
const currentAnalysis = (row: Row): BulkFileAnalysis | null => (row.checked && row.checked.token === row.token ? row.checked.analysis : null);
const rowState = (row: Row): BulkRowState => ({ key: row.key, name: row.name, analysis: currentAnalysis(row), addAnyway: row.addAnyway, result: row.result });
const thumbOf = (row: Row): string | null => row.checked?.analysis.thumbUrl ?? null;
const outfitsWord = (count: number): string => `${count.toLocaleString('en-US')} outfit${count === 1 ? '' : 's'}`;

/** The registry to offer: the one the catalog brought, or the one a new category's save answered when that is newer. */
function newerRegistry(fromCatalog: CategoriesView | null, saved: CategoriesView | null): CategoriesView | null {
  return saved && (!fromCatalog || saved.revision > fromCatalog.revision) ? saved : fromCatalog;
}

/** A row's picture on the card's wallpaper; dimmed while the file is checked again. */
function Picture({ url, wallpaper, pending }: { url: string | null; wallpaper: Wallpaper; pending: boolean }) {
  return (
    <span className={['ui-row-pic', wallpaperClass(wallpaper), pending && url ? 'is-pending' : ''].filter(Boolean).join(' ')}>
      {url ? <img src={url} alt="" />
        : pending ? <Icon icon={Loader2} size="sm" className="ui-spin" />
          : <Icon icon={ImageOff} size="md" className="ui-row-pic-none" />}
    </span>
  );
}

/**
 * Outfits and mascot › Gallery › Add several outfits (owner, 2026-10-09: "allows me to add categories and upload
 * outfit images", "it will need to allow batch upload"): choose many PNG or WebP files, a whole folder, or drop them; pick
 * the category (or type a new one, saved first) and the gallery window for all of them; check the list (each file's
 * picture as desktops will show it, its name from its file name, its size, Mirror (face left), and its status: ready, to be
 * prepared for desktops, already in the gallery, a refusal, or no room); then Add sends them one at a time, each with the
 * revision the previous answer carried, with a progress bar and Stop (which finishes the one being sent). Files are
 * checked one at a time and each picture's object URL is revoked when its row goes, so a list of 50 or more stays light.
 * The rules and the wording are in lib/bulkOutfits.ts; the canvas work in mascotCanvas.ts.
 */
export function BulkOutfitsSection({ catalog, now, timeZone, wallpaper, disabled, waiting, onCatalog, onBusy, reload, analyze = analyzeOutfitFile }: BulkOutfitsSectionProps) {
  const [rows, setRowsState] = useState<Row[]>([]);
  const [leftOut, setLeftOut] = useState<LeftOut[]>([]);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const [category, setCategory] = useState<string>(GENERAL_CATEGORY_ID);
  const [windowInputs, setWindowInputs] = useState<WindowInputs>(() => defaultWindowInputs(now));
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [savedRegistry, setSavedRegistry] = useState<CategoriesView | null>(null);
  const [categoryNote, setCategoryNote] = useState<Note | null>(null);
  const [uploading, setUploading] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  // Raised when a check ends, so the next file's check starts even when the finished one's row has gone.
  const [checks, setChecks] = useState(0);
  // The rows as last changed, read by the checks and the upload between renders (updateRows keeps it and the state as one).
  const rowsRef = useRef<Row[]>([]);
  const keySeed = useRef(0);
  const checking = useRef(false);
  const mounted = useRef(false);
  const stop = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const chooseButton = useRef<HTMLButtonElement>(null);
  const removeButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusNext = useRef<string | null>(null);
  // Each row's own Mirror choice from just before "Mirror all" turned every row on: pressing it again puts them back.
  const mirrorBefore = useRef<Map<string, boolean> | null>(null);

  /** Change the rows; a picture no row shows any more (its row removed, or a new check replaced it) is revoked at once. */
  const updateRows = useCallback((change: (rows: Row[]) => Row[]) => {
    const before = rowsRef.current;
    const after = change(before);
    if (after === before) return;
    rowsRef.current = after;
    setRowsState(after);
    const shown = new Set(after.map(thumbOf));
    for (const row of before) {
      const url = thumbOf(row);
      if (url && !shown.has(url)) URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Leaving the page stops an upload after the outfit being sent, and lets go of every picture.
      stop.current = true;
      for (const row of rowsRef.current) {
        const url = thumbOf(row);
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, []);

  // A folder picker: the browser lists every file in the folder chosen (React has no prop for the attribute).
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
  }, []);

  // One file is checked at a time, top to bottom, so only one image is decoded and prepared at once.
  useEffect(() => {
    if (checking.current) return;
    const next = rows.find((row) => row.checked?.token !== row.token);
    if (!next) return;
    checking.current = true;
    const { key, token, file, mirror } = next;
    void (async () => {
      let analysis: BulkFileAnalysis;
      try {
        analysis = await analyze(file, mirror);
      } catch {
        analysis = unreadableAnalysis(file.size);
      }
      checking.current = false;
      if (!mounted.current) {
        if (analysis.thumbUrl) URL.revokeObjectURL(analysis.thumbUrl);
        return;
      }
      // The row may have gone, or been mirrored again, meanwhile: then this check is not shown, and its picture goes.
      const wanted = rowsRef.current.some((row) => row.key === key && row.token === token);
      if (wanted) updateRows((list) => list.map((row) => (row.key === key && row.token === token ? { ...row, checked: { token, analysis } } : row)));
      else if (analysis.thumbUrl) URL.revokeObjectURL(analysis.thumbUrl);
      setChecks((count) => count + 1);
    })();
  }, [rows, checks, analyze, updateRows]);

  // After a row is removed, the focus moves to the row that took its place (or to Choose files…), never to the page.
  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    focusNext.current = null;
    (target === CHOOSE_FILES ? chooseButton.current : removeButtons.current.get(target))?.focus();
  });

  const categoriesOffered = catalog.categories !== undefined;
  const registry = newerRegistry(catalog.categories ?? null, savedRegistry);
  const categoryOptions = registry?.categories ?? [];
  // A category another admin removed meanwhile, or categories that cannot be read just now: General.
  const chosenCategory = registry && categoryOptions.some((option) => option.id === category) ? category : GENERAL_CATEGORY_ID;
  const categoryName = categoryNameOf(chosenCategory === GENERAL_CATEGORY_ID ? null : chosenCategory, categoryOptions);
  const statuses = bulkStatuses(rows.map(rowState), catalog, current);
  const stillChecking = statuses.filter((status) => status.kind === 'checking').length;
  const planned = statuses.filter(isUploadable).length;
  const changeable = rows.filter((row) => row.result?.kind !== 'added');
  const allMirrored = changeable.length > 0 && changeable.every((row) => row.mirror);
  const full = roomOf(catalog).room === 0;
  // The pickers, the category and the window, while nothing can be added (the list below is never locked by it). While
  // the upload runs they wait with it, even once it fills the gallery.
  const locked = disabled && !uploading;
  const canUpload = !disabled && !waiting && !uploading && !naming && !savingCategory && stillChecking === 0 && planned > 0;
  const roomLine = uploading ? null : roomText(catalog, statuses);
  const leftOutLine = leftOutText(leftOut);
  const currentName = current ? rows.find((row) => row.key === current)?.name.trim() ?? null : null;
  const waitHint = disabled ? null
    : waiting ? 'Waiting for the other change to finish.'
      : naming ? 'Save or cancel the new category first.'
        : stillChecking > 0 ? `Checking ${stillChecking.toLocaleString('en-US')} more file${stillChecking === 1 ? '' : 's'}…`
          : planned === 0 && statuses.some((status) => status.kind !== 'added') ? 'No file in the list can be added as it is: each row says why.' : null;

  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const { accepted, leftOut: skipped } = intake(files, rowsRef.current.map((row) => row.file));
    setLeftOut(skipped);
    setNote(null);
    if (accepted.length === 0) return;
    updateRows((list) => [
      ...list,
      ...accepted.map((file): Row => ({
        key: `bulk-${(keySeed.current += 1)}`,
        file,
        name: nameFromFileName(file.name),
        mirror: false,
        token: 0,
        checked: null,
        addAnyway: false,
        result: null,
      })),
    ]);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (locked || uploading) return;
    const transfer = event.dataTransfer;
    // A dropped folder comes as an entry to read; the entries must be taken while the drop event lasts.
    const entries = Array.from(transfer.items ?? [])
      .map((item) => (item.kind === 'file' && typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))
      .filter((entry): entry is FileSystemEntry => entry !== null);
    if (!entries.some((entry) => entry.isDirectory)) {
      addFiles(Array.from(transfer.files ?? []));
      return;
    }
    setReading(true);
    void filesFromEntries<File>(entries).then((files) => {
      if (!mounted.current) return;
      setReading(false);
      addFiles(files);
    });
  };

  const fromPicker = (input: HTMLInputElement) => {
    const files = Array.from(input.files ?? []);
    input.value = ''; // the same files can be chosen again
    addFiles(files);
  };

  const patchRow = (key: string, patch: (row: Row) => Partial<Row>) => {
    updateRows((list) => list.map((row) => (row.key === key ? { ...row, ...patch(row) } : row)));
  };
  /** A renamed or mirrored row that was not added is tried again by the next Add. */
  const retried = (row: Row): BulkResult | null => (row.result?.kind === 'failed' ? null : row.result);

  /** The row mirrored or not, checked again when that changes (a row already added stays as it went up). */
  const mirrored = (row: Row, mirror: boolean): Row => (row.result?.kind === 'added' || row.mirror === mirror ? row : { ...row, mirror, token: row.token + 1, result: retried(row) });
  const setMirror = (key: string, mirror: boolean) => {
    // A row's own choice after Mirror all: from now on, pressing Mirror all again turns every row off.
    mirrorBefore.current = null;
    patchRow(key, (row) => ({ mirror, token: row.token + 1, result: retried(row) }));
  };
  /**
   * Mirror all (face left): every row mirrored, each row's own choice remembered. Pressed again, it puts those choices
   * back, so a row mirrored on its own stays mirrored (an outfit that faces right never goes up unmirrored because
   * Mirror all was tried and undone); with no choice to put back (every row was mirrored one by one), every row is off.
   */
  const mirrorAll = () => {
    if (!allMirrored) {
      mirrorBefore.current = new Map(rowsRef.current.map((row) => [row.key, row.mirror]));
      updateRows((list) => list.map((row) => mirrored(row, true)));
      return;
    }
    const before = mirrorBefore.current;
    mirrorBefore.current = null;
    updateRows((list) => {
      const restored = before ? list.map((row) => mirrored(row, before.get(row.key) ?? false)) : list;
      return restored.some((row, index) => row !== list[index]) ? restored : list.map((row) => mirrored(row, false));
    });
  };

  const remove = (key: string) => {
    const list = rowsRef.current;
    const at = list.findIndex((row) => row.key === key);
    if (at < 0) return;
    const after = list[at + 1] ?? list[at - 1];
    focusNext.current = after ? after.key : CHOOSE_FILES;
    updateRows((rowsNow) => rowsNow.filter((row) => row.key !== key));
  };

  const clearList = () => {
    mirrorBefore.current = null;
    updateRows(() => []);
    setLeftOut([]);
    setNote(null);
    setProgress(null);
    focusNext.current = CHOOSE_FILES;
  };

  const onCategoryChange = (value: string) => {
    setCategoryNote(null);
    if (value === NEW_CATEGORY) {
      setNaming(true);
      return;
    }
    setCategory(value);
    setNaming(false);
    setNewName('');
  };

  const cancelNaming = () => {
    setNaming(false);
    setNewName('');
    setCategoryNote(null);
  };

  /** "New category…": the registry is saved with it first, so every outfit in the batch can go in it. */
  const saveCategory = async () => {
    if (!registry || savingCategory) return;
    const made = newCategory(registry, newName);
    if (made.kind === 'refused') {
      setCategoryNote({ ok: false, text: made.message });
      return;
    }
    if (made.kind === 'existing') {
      setCategory(made.id);
      cancelNaming();
      setCategoryNote({ ok: true, text: `“${made.name}” is already a category, so these outfits go in it.` });
      return;
    }
    setSavingCategory(true);
    setCategoryNote(null);
    try {
      const saved = await api.saveDesktopMascotCategories(made.body);
      setSavedRegistry(saved);
      setCategory(made.id);
      setNaming(false);
      setNewName('');
      setCategoryNote({ ok: true, text: `Saved the category “${made.name}”; these outfits go in it. It is shown all year: give it a season under Categories below if it needs one.` });
      await reload();
    } catch (error) {
      if (mascotErrorCode(error) === 'mascot_categories_changed') {
        setCategoryNote({ ok: false, text: 'Another admin changed the categories just now, so the new one was not saved. The categories have been read again; save it again.' });
        await reload();
      } else {
        setCategoryNote({ ok: false, text: mascotAuthText(error, 'save') ?? errorText(error, 'The category was not saved.') });
      }
    } finally {
      setSavingCategory(false);
    }
  };

  /**
   * Add: read the gallery again, take the rows that fit it now, and send them one at a time, each with the revision the
   * previous answer carried (POST …/outfits with the name, the window, the category, never featured). After each answer
   * the gallery above follows; a refusal of that image or name skips the row, another admin's change is sent again with
   * the revision read again, and a refusal the next add would get too stops the upload. Stop finishes the outfit being
   * sent. The catalog is read again at the end.
   */
  const upload = async () => {
    if (!canUpload) return;
    const windowProblem = windowRefusal(windowFromInputs(windowInputs), clockNow());
    if (windowProblem) {
      setNote({ ok: false, text: windowProblem });
      return;
    }
    const categoryId = chosenCategory;
    const where = categoryName;
    stop.current = false;
    setStopping(false);
    setUploading(true);
    setNote(null);
    onBusy(true);
    // The gallery as it is now: its revision, and what the rows are checked against before anything is sent.
    let first: DesktopMascotCatalog | null = null;
    let readProblem: unknown = null;
    try {
      first = await api.desktopMascot();
    } catch (error) {
      readProblem = error;
    }
    if (!first) {
      setUploading(false);
      onBusy(false);
      const why = readProblem === null
        ? 'the mascot gallery is not on this service any more.'
        : mascotAuthText(readProblem, 'read') ?? errorText(readProblem, 'the mascot gallery could not be read just now. Try again in a minute.');
      setNote({ ok: false, text: `Nothing was added: ${why}` });
      return;
    }
    let latest: DesktopMascotCatalog = first;
    onCatalog(first);
    const snapshot = rowsRef.current;
    const fits = bulkStatuses(snapshot.map(rowState), latest, null);
    const plan = snapshot.filter((_, index) => isUploadable(fits[index]!));
    if (plan.length === 0) {
      setUploading(false);
      onBusy(false);
      setNote({ ok: false, text: 'Nothing was added: the gallery changed meanwhile, and each row now says why it cannot be added.' });
      return;
    }
    setProgress({ done: 0, total: plan.length });
    let revision = latest.revision;
    let added = 0;
    let failed = 0;
    let queued = 0;
    let stopped: 'user' | 'error' | null = null;
    let reason: string | undefined;

    // The catalog the card shows: handed over after each row's result is set, so a row just added never reads, for a
    // moment, as its own image already in the catalog.
    let shown: DesktopMascotCatalog = first;

    /** Read the gallery again after a refusal; null when it cannot be read. */
    const reread = async (): Promise<DesktopMascotCatalog | null> => {
      try {
        const fresh = await api.desktopMascot();
        if (fresh) {
          latest = fresh;
          revision = fresh.revision;
        }
        return fresh;
      } catch {
        return null;
      }
    };

    /** One row: its result, and the sentence that stops the upload when the next add would be refused too. */
    const send = async (row: Row): Promise<{ result: BulkResult; stop?: string; queued?: boolean }> => {
      const analysis = currentAnalysis(row);
      if (!analysis?.upload) return { result: { kind: 'failed', text: 'The file is not checked; check the list and add it again.' } };
      let pngBase64: string;
      try {
        pngBase64 = bytesToBase64(new Uint8Array(await analysis.upload.arrayBuffer()));
      } catch (error) {
        return { result: { kind: 'failed', text: errorText(error, 'The file could not be read. Choose it again.') } };
      }
      for (let attempt = 0; ; attempt += 1) {
        const ready = outfitAdd({ pngBase64, name: row.name, window: windowInputs, featured: false, category: categoryId }, revision, clockNow());
        if (ready.kind === 'refused') return { result: { kind: 'failed', text: ready.message } };
        const before = new Set(latest.outfits.map((outfit) => outfit.id));
        try {
          const answer = await api.addDesktopMascotOutfit(ready.body);
          latest = answer;
          revision = answer.revision;
          return { result: { kind: 'added', outfitId: answer.outfitId, note: null } };
        } catch (error) {
          const action = uploadErrorAction(error, attempt);
          const named = () => [...latest.outfits, ...(latest.retired ?? []), ...(latest.orphans ?? [])];
          if (action === 'skip') return { result: { kind: 'failed', text: mascotErrorText(error, named(), 'The outfit was not added.', 'add') } };
          const fresh = await reread();
          const text = mascotErrorText(error, named(), 'The outfit was not added.', 'add');
          if (action === 'retry' && fresh) continue;
          const found = fresh ? addedSince(fresh, before, analysis.sha256, row.name) : null;
          if (action === 'saved') return { result: { kind: 'added', outfitId: found?.id ?? null, note: text }, queued: true };
          if (action === 'check') {
            if (found) return { result: { kind: 'added', outfitId: found.id, note: LOST_ANSWER_TEXT } };
            return { result: { kind: 'failed', text: NO_ANSWER_TEXT }, stop: NO_ANSWER_TEXT };
          }
          return { result: { kind: 'failed', text }, stop: text };
        }
      }
    };

    let done = 0;
    for (const row of plan) {
      if (stop.current) {
        stopped = 'user';
        break;
      }
      setCurrent(row.key);
      const outcome = await send(row);
      updateRows((list) => list.map((item) => (item.key === row.key ? { ...item, result: outcome.result } : item)));
      if (latest !== shown) {
        shown = latest;
        onCatalog(latest);
      }
      done += 1;
      setProgress({ done, total: plan.length });
      if (outcome.result.kind === 'added') added += 1;
      else failed += 1;
      if (outcome.queued) queued += 1;
      if (outcome.stop) {
        stopped = 'error';
        reason = outcome.stop;
        break;
      }
    }
    setCurrent(null);
    setUploading(false);
    setStopping(false);
    setProgress(null);
    onBusy(false);
    setNote(uploadSummaryText({ added, failed, planned: plan.length, stopped, reason, category: where, queued }));
    await reload();
  };

  return (
    <section aria-labelledby="mascot-bulk-title" className="ui-section-host">
      <Panel
        heading="h3"
        titleId="mascot-bulk-title"
        title={<span className="ui-title-icon"><Icon icon={Images} size="md" />Add several outfits</span>}
        description="A whole folder of outfits at once, all into one category"
        footer={(
          <span>
            Each outfit is named from its file name, which you can change in the list. {MASCOT_DRAW_HINT} Turn on Mirror for any outfit that faces
            right; there is no need to flip the files first. A file that is too large for desktops is prepared here first, as under Add
            an outfit. Nothing is added until you press Add, and none of them is featured.
          </span>
        )}
      >
        <div className="ui-form">
          {full && !uploading && <Callout tone="warn">{fullText(catalog)}</Callout>}
          <fieldset disabled={locked} className="ui-fieldset">
            <div className="ui-form">
              <div
                data-drop-zone=""
                className={dragging ? 'ui-dropzone is-dragging' : 'ui-dropzone'}
                onDragOver={(event) => {
                  if (locked || uploading) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'copy';
                  setDragging(true);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
                }}
                onDrop={onDrop}
              >
                <div className="ui-actions">
                  <Button ref={chooseButton} icon={Upload} disabled={uploading} onClick={() => fileInput.current?.click()}>Choose files…</Button>
                  <Button icon={FolderOpen} disabled={uploading} onClick={() => folderInput.current?.click()}>Choose a folder…</Button>
                  <span className="ui-cell-sub">or drop PNG or WebP files, or a whole folder, here.</span>
                  {reading && <Icon icon={Loader2} size="md" className="ui-spin" label="Reading the folder" />}
                </div>
                <span className="ui-cell-sub">
                  Each one a PNG (or WebP) with a transparent background; a JPEG photo cannot be an outfit. Up to {BULK_MAX_FILES} files in the list at once.
                </span>
                <input ref={fileInput} type="file" multiple accept="image/png,image/webp,.png,.webp" hidden aria-label="Outfit files" onChange={(event) => fromPicker(event.target)} />
                <input ref={folderInput} type="file" hidden aria-label="Outfit folder" onChange={(event) => fromPicker(event.target)} />
              </div>
              {leftOutLine && <p className="ui-note is-warn" role="status"><span>{leftOutLine}</span></p>}

              {categoriesOffered && (
                <Field
                  label="Category"
                  help={registry === null
                    ? 'Categories cannot be read just now, so these outfits go in General. Reload in a minute to choose a category.'
                    : 'The section people see these outfits under. A category that is switched off or out of season hides its outfits until it is on again.'}
                >
                  <div className="ui-actions">
                    <Select
                      id="mascot-bulk-category"
                      className="ui-w-md"
                      value={chosenCategory}
                      disabled={uploading || savingCategory || registry === null}
                      onChange={(event) => onCategoryChange(event.target.value)}
                    >
                      <option value={GENERAL_CATEGORY_ID}>{GENERAL_CATEGORY_NAME}</option>
                      {categoryOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}{!option.enabled ? ' (switched off)' : option.live ? '' : ' (out of season)'}
                        </option>
                      ))}
                      <option value={NEW_CATEGORY}>New category…</option>
                    </Select>
                    {naming && (
                      <>
                        <Input
                          aria-label="Name of the new category"
                          placeholder="NFL"
                          maxLength={CATEGORY_NAME_MAX}
                          value={newName}
                          autoFocus
                          disabled={savingCategory}
                          className="ui-w-md"
                          onChange={(event) => setNewName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void saveCategory();
                            } else if (event.key === 'Escape') {
                              event.preventDefault();
                              cancelNaming();
                            }
                          }}
                        />
                        <Button size="sm" icon={Check} busy={savingCategory} disabled={newName.trim().length === 0} onClick={() => void saveCategory()}>Save category</Button>
                        <Button variant="ghost" size="sm" icon={X} disabled={savingCategory} onClick={cancelNaming}>Cancel</Button>
                      </>
                    )}
                  </div>
                </Field>
              )}
              {categoriesOffered && <SaveNote note={categoryNote} />}
              <WindowFields idPrefix="mascot-bulk" value={windowInputs} now={now} timeZone={timeZone} disabled={uploading} onChange={setWindowInputs} />
            </div>
          </fieldset>

          {/*
            The list is outside the fieldset: once the gallery is full (an upload that filled it, or another admin), its rows
            can still be removed, cleared, renamed or mirrored, ready for when outfits are retired; Add stays off meanwhile.
            Its own controls wait while the upload runs.
          */}
          {rows.length > 0 && (
            <div className="ui-form">
              <div className="ui-actions">
                <Button
                  size="sm"
                  icon={FlipHorizontal2}
                  aria-pressed={allMirrored}
                  disabled={uploading || changeable.length === 0}
                  onClick={mirrorAll}
                >
                  Mirror all (face left)
                </Button>
                <Button variant="ghost" size="sm" icon={ListX} disabled={uploading} onClick={clearList}>Clear the list</Button>
              </div>
              <p className="ui-cell-sub">{MIRROR_WARNING}</p>
              <p className="ui-para">{countLine(statuses)}</p>
              {roomLine && (
                <p className={statuses.some((status) => status.kind === 'no-room') ? 'ui-para ui-text-warn' : 'ui-para ui-cell-sub'}>{roomLine}</p>
              )}
              <div className="ui-table-box ui-bulk-list">
                <table className="ui-table ui-table--tall" aria-label="Outfits to add">
                  <thead>
                    <tr>
                      <th scope="col">Picture</th>
                      <th scope="col">Name</th>
                      <th scope="col">Size</th>
                      <th scope="col">Mirror (face left)</th>
                      <th scope="col">Status</th>
                      <th scope="col"><span className="ui-sr">Remove</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const status = statuses[index]!;
                      const analysis = currentAnalysis(row);
                      const shown = row.checked?.analysis ?? null;
                      const label = row.name.trim() || row.file.name;
                      const addedAlready = row.result?.kind === 'added';
                      const rowLocked = uploading || addedAlready;
                      const detail = 'detail' in status ? status.detail : null;
                      const detailClass = status.kind === 'refused' || status.kind === 'failed' ? 'ui-cell-sub ui-text-bad'
                        : status.kind === 'same-name' || status.kind === 'name' || status.kind === 'no-room' ? 'ui-cell-sub ui-text-warn' : 'ui-cell-sub';
                      return (
                        <tr key={row.key} data-row={row.key}>
                          <td className="ui-bulk-pic"><Picture url={thumbOf(row)} wallpaper={wallpaper} pending={analysis === null} /></td>
                          <td>
                            <div className="ui-cell-stack">
                              <Input
                                size="sm"
                                aria-label={`Outfit name for ${row.file.name}`}
                                maxLength={MASCOT_LIMITS.nameMax}
                                value={row.name}
                                disabled={rowLocked}
                                className="ui-w-md"
                                onChange={(event) => {
                                  const name = event.target.value;
                                  patchRow(row.key, (item) => ({ name, result: retried(item) }));
                                }}
                              />
                              <span className="ui-cell-sub ui-clip ui-w-md" title={row.file.name}>{row.file.name}</span>
                            </div>
                          </td>
                          <td className="is-num">
                            {shown ? (
                              <span className="ui-cell-stack">
                                {shown.width > 0 && <span>{shown.width} x {shown.height} px</span>}
                                <span className="ui-cell-sub">{formatBytes(shown.sizeBytes)}</span>
                              </span>
                            ) : '…'}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              icon={FlipHorizontal2}
                              aria-pressed={row.mirror}
                              aria-label={`Mirror ${label} (face left)`}
                              disabled={rowLocked}
                              onClick={() => setMirror(row.key, !row.mirror)}
                            >
                              Mirror
                            </Button>
                          </td>
                          <td className="ui-bulk-status">
                            <div className="ui-cell-stack">
                              <span>
                                <StatusChip tone={STATUS_TONE[status.kind]} icon={status.kind === 'checking' || status.kind === 'uploading' ? Loader2 : undefined}>
                                  {statusLabel(status)}
                                </StatusChip>
                              </span>
                              {detail && <span className={detailClass}>{detail}</span>}
                              {(status.kind === 'same-name' || (row.addAnyway && !addedAlready)) && (
                                <Checkbox label="Add anyway" checked={row.addAnyway} disabled={rowLocked} onChange={(event) => patchRow(row.key, () => ({ addAnyway: event.target.checked }))} />
                              )}
                              {status.kind === 'failed' && (
                                <span><Button size="sm" icon={RotateCcw} disabled={uploading} onClick={() => patchRow(row.key, () => ({ result: null }))}>Try again</Button></span>
                              )}
                              {analysis && analysis.warnings.length > 0 && isUploadable(status) && (
                                <details className="ui-details ui-details--inline">
                                  <summary className="ui-cell-sub">{analysis.warnings.length === 1 ? '1 note' : `${analysis.warnings.length} notes`}</summary>
                                  {analysis.warnings.map((warning) => <p key={warning} className="ui-cell-sub ui-text-warn">{warning}</p>)}
                                </details>
                              )}
                            </div>
                          </td>
                          <td className="is-end">
                            <Button
                              ref={(element) => {
                                if (element) removeButtons.current.set(row.key, element);
                                else removeButtons.current.delete(row.key);
                              }}
                              variant="ghost"
                              size="sm"
                              icon={X}
                              aria-label={`Remove ${label} from the list`}
                              title="Remove from the list"
                              disabled={uploading}
                              onClick={() => remove(row.key)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div className="ui-form">
              <div className="ui-actions">
                <Button variant="primary" icon={uploading ? Loader2 : Check} disabled={!canUpload} onClick={() => void upload()}>
                  {uploading ? 'Adding…' : `Add ${outfitsWord(planned)}${categoriesOffered ? ` to ${categoryName}` : ''}`}
                </Button>
                {uploading && (
                  <Button icon={CircleStop} disabled={stopping} onClick={() => { stop.current = true; setStopping(true); }}>
                    {stopping ? 'Stopping after this one…' : 'Stop after this one'}
                  </Button>
                )}
                {!uploading && waitHint && <span className="ui-cell-sub">{waitHint}</span>}
              </div>
              {uploading && progress && (
                <div className="ui-form">
                  <Meter count={{ done: progress.done, total: progress.total }} label="Outfits sent" tone="ok" />
                  <p className="ui-cell-sub" aria-live="polite">
                    {progress.done.toLocaleString('en-US')} of {progress.total.toLocaleString('en-US')}
                    {currentName ? ` · adding “${currentName}”` : ''}
                  </p>
                </div>
              )}
            </div>
          )}
          <SaveNote note={note} />
        </div>
      </Panel>
    </section>
  );
}
